import axios, { AxiosResponse } from "axios";

import APIRequest, { HTTPRequestMethod, Request } from "./APIRequest";
import { ASuiteModel, IUser } from "./ASuiteModels";


/**
 * API request constructor to ASuite main API Heimdall. 
 */
export default class Heimdall {

    
    /**
     * Authentication token stored as global state in Thomas 
     * class. Set upon successful user authentication. 
     */
    private static APIToken: string | undefined = undefined;

    /**
     * Base URL of API. Stored in environment for seamless
     * local development and production build. 
     */
    private static baseURL: string = process.env.REACT_APP_API_URL;

    /**
     * Construct request header with dynamic parameters.
     * @param token API access token. 
     * @param contentType Request content type. Default: application/json
     */
    private static getHeaders = (token: string, contentType?: string, rHeaders?: Record<string, string | number>): Record<string, string | number> => {
        return {
            "Authorization": `Bearer ${token}`,
            "Content-Type": contentType ?? "application/json",
            ...rHeaders
        }
    }

    /**
     * Set static class instance property.
     * API authentication 'Bearer' token. 
     * @param token 
     */
    public static setToken = (token: string | undefined): void => {
        Heimdall.APIToken = token
    };

    /**
     * Retrieve user for SSO 
     * cookie auth token. 
     */
    public static async authenticate(): Promise<IUser> {
        return await Heimdall.request<IUser>(APIRequest.auth) as IUser;
    }

    /**
     * Helper function to construct 
     * request API route with any ID args. 
     */
    private static getRoute(r: (...args: any[]) => string, data?: Record<string | number | symbol, any>): string {
        const id: string | number = data?.id ?? ""
        const secret: string = data?.secret ?? ""
        return Heimdall.baseURL + r(...[id, secret])
    }   

    /**
     * Dispatch request to Heimdall via axios. 
     * @param r Request with HTTP method, route and required payload keys for request. {@link Request}
     * @param data Payload. 
     * @param rawResponse Flag if raw response from API call should be returned or error checked result.
     */
    public static async request<I extends ASuiteModel | File>(r: Request, data?: Record<string | number | symbol, any>, rawResponse?: boolean): Promise<I | Array<I> | AxiosResponse<I>> {

        // Requests cannot be dispatched wihout 
        // instantiated caller set in user authentication. 
        if (Heimdall.APIToken === undefined && !r.public) 
            throw new Error("FATAL ERROR: Heimdall APIToken not set.")

        const {method, route, keys, contentType, headers: rHeaders, opts} = r;

        keys.forEach((key: string) => {
            if (!(key in data)) {
                // Handle URLSearchParams key check 
                const asSearchParams = data as URLSearchParams;
                if (asSearchParams !== null && [...asSearchParams.keys()].includes(key)) return;
                throw new Error(`FATAL ERROR: Missing required key: ${key} in payload.`)
            }
        })

        // Setup payload - "GET" HTTP method does not support 
        // sending request bodies. Replace with "params" key. 
        const payloadKey: string = method === HTTPRequestMethod.GET ? "params" : "data"
        const payload: Record<string, Record<string, unknown>> = {[payloadKey]: data}

        // Setup request URL with provided 
        // route and any ID args
        const url: string = Heimdall.getRoute(route, data)

        // Configure request headers for API request
        const headers = Heimdall.getHeaders(Heimdall.APIToken, contentType, rHeaders)

        // Compose request
        const req: Record<string, any> = {
            "headers": headers,
            "url": url,
            "method": method,
            ...opts,
            ...payload
        }

        // Dispatch request
        try {
            const res: AxiosResponse = await axios(req);
            if (rawResponse) return res;
            if (res.status !== 200) throw new Error(res.data);
            return res.data as I;
        } catch (e) {
            console.log("ERROR RESPONSE", e.response)
            throw e
        }
    }
}
